/**
 * R7E_CacheManager
 * 
 * Manages cache mode state for Recognyze applications.
 */

class R7E_CacheManager {
    constructor(globalCachePrefix = 'r7e_cache.') {
        this.globalCachePrefix = globalCachePrefix;
        this.caches = [];
    }
    addCache(cache) {
        this.caches.push(cache);
    }
    getCachePrefix(cachePrefix) {
        return this.globalCachePrefix + cachePrefix;
    }
    getCache(cachePrefix) {
        let cache = this.caches.find(c => c.cachePrefix === this.getCachePrefix(cachePrefix));
        if (!cache) {
            cache = new R7E_Cache(this.getCachePrefix(cachePrefix));
            this.addCache(cache);
        }
        return cache;
    }
}

class R7E_Cache {
    expirationTime = 86400000; // 24 hours (default)
    constructor(cachePrefix) {
        this.enabled = false;
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                this.enabled = true;
                if (window.localStorage.getItem(`${cachePrefix}cache_enabled`) === '0') {
                    this.enabled = false;
                }
            }
        } catch (e) {
            // Ignore localStorage errors (e.g., in private mode)
        }
        this.CACHE_PREFIX = cachePrefix;
        this.EXPIRATION_SETTING_KEY = cachePrefix + 'expiration';
        this.DEFAULT_EXPIRATION_MS = this.toMilliseconds('24hours'); // Default cache expiration: 24 hours (86400000 ms)
        this.expirationTime = this.getCacheExpirationTimeFromSettings();
    }

    // Cache expiration options (in milliseconds)
    toMilliseconds(value) {
        if (typeof value === 'string') {
                switch (value) {
                case '1hour':
                    return 3600000;      // 1 hour
                case '6hours':
                    return 21600000;    // 6 hours
                case '24hours':
                    return 86400000;   // 24 hours (default)
                case '7days':
                    return 604800000;     // 7 days
            }
        } else if (typeof value === 'number') {
            return value;
        }
        return this.DEFAULT_EXPIRATION_MS;
    }
    isKnownCacheExpirationTime(value) {
        return typeof value === 'string' && ['1hour', '6hours', '24hours', '7days'].includes(value);
    }

    /**
     * Get cache expiration time from settings
     * @returns {number} Expiration time in milliseconds
     */
    getCacheExpirationTimeFromSettings() {
        if (!this.enabled) {
            return this.DEFAULT_EXPIRATION_MS;
        }        
        const stored = localStorage.getItem(this.EXPIRATION_SETTING_KEY);
        return this.toMilliseconds(stored);
    }

    /**
     * Set cache expiration time in settings
     * @param {string} option - One of: '1hour', '6hours', '24hours', '7days'
     */
    setCacheExpirationTime(option) {
        if (!this.enabled) {
            return;
        }
        if (this.isKnownCacheExpirationTime(option)) {
            localStorage.setItem(this.EXPIRATION_SETTING_KEY, option);
        } else if (typeof option === 'number') {
            localStorage.setItem(this.EXPIRATION_SETTING_KEY, option.toString());
        } else {
            console.error('Invalid cache expiration time:', option);
        }
        this.expirationTime = this.getCacheExpirationTimeFromSettings();
    }

    /**
     * Check if cache entry is still valid
     * @param {object} cacheEntry - Cache entry with timestamp and expiresAt
     * @returns {boolean} True if cache is valid
     */
    isCacheValid(cacheEntry) {
        if (!cacheEntry || !cacheEntry.expiresAt) {
            return false;
        }
        const now = Date.now();
        return now < cacheEntry.expiresAt;
    }

    /**
     * Get cached data if available and valid
     * @param {string} k - Cache key
     * @returns {object|null} Cached data or null if not found/invalid
     */
    getCachedData(k) {
        if (!this.enabled) {
            return null;
        }
        try {
            const cacheKey = `${this.CACHE_PREFIX}${k}`;
            const cached = localStorage.getItem(cacheKey);
            if (!cached) {
                return null;
            }
            const cacheEntry = JSON.parse(cached);
            if (this.isCacheValid(cacheEntry)) {
                console.log('Cache ' + cacheKey + ' is valid, returning data');
                return cacheEntry.data;
            } else {
                // Remove expired cache entry
                localStorage.removeItem(cacheKey);
                console.log('Cache ' + cacheKey + ' is expired, removing and returning null');
                return null;
            }
        } catch (error) {
            console.error('Error reading R7E cache:', error);
            return null;
        }
    }

    /**
     * Store data in cache with expiration
     * @param {string} k - Cache key
     * @param {object} data - Data to cache
     */
    setCachedData(k, data, retry = false) {
        if (!this.enabled) {
            return;
        }
        const cacheKey = `${this.CACHE_PREFIX}${k}`;

        try {
            const now = Date.now();
            const cacheEntry = {
                data: data,
                timestamp: now,
                expiresAt: now + this.expirationTime
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
        } catch (error) {
            // Handle quota exceeded or other storage errors
            if (error.name === 'QuotaExceededError' || error.code === 22) {
                console.warn('localStorage quota exceeded, clearing old R7E cache entries');
                // Clear cache for this author to make space
                this.clearCache(k);
                // Try again
                if (!retry) {
                    this.setCachedData(k, data, true);
                } else {
                    console.error('Failed to cache R7E data after cleanup:', error);
                }
            } else {
                console.error('Error caching R7E data:', error);
            }
        }
    }

    /**
     * Clear R7E cache
     * @param {string|null} k - If provided, clear cache for specific key only. If null, clear all R7E cache.
     */
    clearCache(k = null) {
        if (!this.enabled) {
            return;
        }
        
        try {
            console.log('Clearing R7E cache for prefix and key: ' + this.CACHE_PREFIX + ' ' + k);
            const keysToRemove = [];
            
            // Iterate through all localStorage keys
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(this.CACHE_PREFIX)) {
                    if (k === null || key === k) {
                        keysToRemove.push(key);
                    }
                }
            }
            
            // Remove all matching keys
            keysToRemove.forEach(key => {
                console.log('Removing R7E cache key: ' + key);
                localStorage.removeItem(key);
            });
            console.log('Removed ' + keysToRemove.length + ' R7E cache keys');
            return keysToRemove.length;
        } catch (error) {
            console.error('Error clearing R7E cache:', error);
            return 0;
        }
    }

    /**
     * Get cache statistics (for debugging/admin purposes)
     * @returns {object} Cache statistics
     */
    getCacheStats() {
        if (!this.enabled) {
            return { enabled: false, totalEntries: 0, totalSize: 0, totalSizeKB: '0.00' };
        }
        
        try {
            let totalEntries = 0;
            let totalSize = 0;
            
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(this.CACHE_PREFIX)) {
                    totalEntries++;
                    const value = localStorage.getItem(key);
                    if (value) {
                        totalSize += value.length;
                    }
                }
            }
            
            return {
                totalEntries,
                totalSize,
                totalSizeKB: (totalSize / 1024).toFixed(2)
            };
        } catch (error) {
            console.error('Error getting cache stats:', error);
            return { totalEntries: 0, totalSize: 0, totalSizeKB: '0.00' };
        }
    }
}

export { R7E_CacheManager };

